/**
 * <variant-picker> — varyant seçici (renk + beden).
 * Radio değişikliğinde:
 *  - URL'i ?variant=<id> ile günceller (history.replaceState)
 *  - Section Rendering API ile product-main section'ı yeniden render eder
 *  - Sayfayı yenilemez
 * Section ID data-section-id attribute'unda.
 */
class VariantPicker extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.productUrl = this.dataset.url;
    this.inputs = Array.from(this.querySelectorAll('[data-vp-input]'));
    this.selectedIdInput = this.querySelector('[data-vp-selected-id]');

    this.onChange = this.onChange.bind(this);
    this.inputs.forEach((i) => i.addEventListener('change', this.onChange));
  }

  getSelectedOptions() {
    const opts = [];
    this.querySelectorAll('fieldset').forEach((fs) => {
      const checked = fs.querySelector('input[type=radio]:checked');
      if (checked) opts.push(checked.value);
    });
    return opts;
  }

  async onChange() {
    const selectedOptions = this.getSelectedOptions();

    // ── 1) SEPET DOĞRULUĞU (en kritik) ──────────────────────────────────
    // Sepete giden gizli name="id"'yi HER ZAMAN, başka hiçbir şeye bağımlı
    // olmadan güncelle. (Eski kod bunu kırık bir section-fetch'in catch'ine
    // bağlamıştı; fetch hep fail edince id ilk varyantta takılıp YANLIŞ renk/
    // beden sepete gidiyordu.)
    const newVariantId = await this.findVariantId(selectedOptions);
    if (newVariantId) {
      this.selectedIdInput.value = newVariantId;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', newVariantId);
      history.replaceState(null, '', url);
    }

    // ── 2) Legend'daki seçili değer etiketi (ANINDA, ağdan bağımsız) ────
    // Sepet id'sinden SONRA ve kendi try/catch'inde: bu dosyanın geçmişinde
    // sepet id'si kendinden önceki bir adıma bağlandığı için kırılmıştı.
    try {
      this.syncSelectedLabels();
    } catch (e) {
      console.warn('Seçili değer etiketi güncellenemedi (sepet id doğru):', e);
    }

    // ── 3) Galeriyi seçilen renge taşı (sepetten bağımsız) ──────────────
    this.syncGallery();

    // ── 4) Fiyat/SKU/renk-adı/ATC tazele (best-effort, Section Rendering) ─
    // Shopify Section Rendering DOĞRU formatı: ?variant=<id>&section_id=<id>
    // (eski "?options=" Shopify tarafından desteklenmez → 0 byte → fail idi).
    if (!newVariantId) return;
    try {
      const fetchUrl = `${this.productUrl}?variant=${newVariantId}&section_id=${this.sectionId}`;
      const res = await fetch(fetchUrl);
      if (!res.ok) return;
      const html = await res.text();
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      const newSection = tmp.querySelector(`[data-section-id="${this.sectionId}"]`) || tmp;
      this.updateField('[data-pm-price]', newSection);
      this.updateField('[data-pm-color]', newSection);
      this.updateField('[data-pm-sku]', newSection);
      this.updateField('[data-pm-atc-text]', newSection);
      this.updateAtcDisabled(newSection);
    } catch (e) {
      console.warn('Section refresh failed (sepet id yine de doğru):', e);
    }
  }

  /**
   * Legend'daki "Renk <seçili değer>" etiketini günceller.
   *
   * Bilerek section-fetch'ten BAĞIMSIZ ve senkron: doğru değer zaten seçili
   * radio'nun value'sunda duruyor, ağ beklemeye gerek yok. Bu dosyanın en
   * başındaki ders aynen geçerli — fetch'e bağlanan alanlar fetch bozulduğunda
   * sessizce bayat kalıyor. Nitekim bu etiket updateField listesinde hiç yoktu:
   * lacivert seçiliyken "Renk Beyaz" yazmaya devam ediyordu (2026-08-08 bulundu).
   *
   * Etiket yalnız Renk option'ında render ediliyor (variant-picker.liquid:32-34);
   * fieldset içinden aradığımız için diğer option'larda sessizce atlanır.
   */
  syncSelectedLabels() {
    this.querySelectorAll('fieldset').forEach((fs) => {
      const label = fs.querySelector('[data-vp-selected]');
      const checked = fs.querySelector('input[type=radio]:checked');
      if (label && checked) label.textContent = checked.value;
    });
  }

  /**
   * Seçili renk radio'sunun data-vp-media-id'sini bulup galeriyi o görsele taşır.
   * Renk değişimi galeriyi değiştirir; beden değişimi galeriyi etkilemez.
   */
  syncGallery() {
    const colorInput = this.querySelector('input[data-vp-media-id]:checked');
    const mediaId = colorInput && colorInput.dataset.vpMediaId;
    if (!mediaId) return;
    const gallery = document.querySelector('product-gallery');
    if (gallery && typeof gallery.goToMedia === 'function') {
      gallery.goToMedia(mediaId);
    }
  }

  updateField(selector, sourceRoot) {
    const target = this.closest('section')?.querySelector(selector) || document.querySelector(selector);
    const source = sourceRoot.querySelector(selector);
    if (target && source) target.innerHTML = source.innerHTML;
  }

  updateAtcDisabled(sourceRoot) {
    const targetAtc = document.querySelector('[data-pm-atc]');
    const sourceAtc = sourceRoot.querySelector('[data-pm-atc]');
    if (targetAtc && sourceAtc) {
      if (sourceAtc.hasAttribute('disabled')) {
        targetAtc.setAttribute('disabled', '');
      } else {
        targetAtc.removeAttribute('disabled');
      }
    }
  }

  /**
   * Client-side variant ID lookup via product JSON (Shopify .js endpoint).
   * URL: <product-url>.js → tüm variant'ları + option array'lerini döner.
   */
  async findVariantId(selectedOptions) {
    if (!this._productData) {
      try {
        const res = await fetch(`${this.productUrl}.js`);
        this._productData = await res.json();
      } catch {
        return null;
      }
    }
    const variant = this._productData.variants.find((v) => {
      return v.options.every((opt, idx) => opt === selectedOptions[idx]);
    });
    return variant?.id;
  }
}

customElements.define('variant-picker', VariantPicker);
